import React, { Fragment, useCallback, useEffect, useState } from 'react';
import withStyles from '@mui/styles/withStyles';
import Button from '@mui/material/Button';
import cx from 'classnames';
import { niceTimeFromString } from '../../util/date';
import { searchCollidingStaffAllocations } from './AppointmentConstants';
import { getCalendarClient } from '../../apollo';
import { withSnackbarMessage } from '../../context/SnackbarMessage';
import { compose } from '../../reactApolloCompat';
import Dialog from '@mui/material/Dialog';
import moment from 'moment';
import { ExclamationTriangleIcon } from '../../component/IconIndex';

const StaffScheduleConflictsPopup = ({ classes, setSnackbarMessage, eventData, checkNow, onSave, onClose }) => {
    const [collisionMsg, setCollisionMsg] = useState(null);
    const [busy, setBusy] = useState(false);

    const onCloseModal = useCallback(() => {
        if (onClose) onClose();
        setCollisionMsg(null);
    }, [onClose]);

    const onError = useCallback(
        (msg, obj) => {
            const errorMessage = msg + (obj && obj.message ? '. Reason: ' + obj.message : '');
            setSnackbarMessage(errorMessage);
            console.error(errorMessage, obj);
        },
        [setSnackbarMessage]
    );

    const checkForCollisions = useCallback(
        input => {
            setBusy(true);
            let variables = null;
            let mapFunc = (input, data) => null;
            if (input.Members && input.Members.length > 0) {
                // Appointments
                variables = {
                    from: moment(input.Date + ' ' + input.TimeFrom).subtract(30, 'minutes'),
                    to: moment(input.DateTo + ' ' + input.TimeTo).add(30, 'minutes'),
                    members: input.Members.map(obj => obj.ID)
                };
                mapFunc = mapAppointmentCollisions;
            } else if (input.StaffAllocations && input.StaffAllocations.length > 0) {
                // Funeral Events
                const eventTo = moment(input.End).add(30, 'minutes');
                const eventMin = moment(input.Start).add(4, 'hours');
                variables = {
                    from: moment(input.Start).subtract(60, 'minutes'),
                    to: eventTo < eventMin ? eventMin : eventTo,
                    members: input.StaffAllocations.filter(
                        obj => !!obj && !!obj.Member && (!!obj.Member.ID || !!obj.MemberID)
                    ).map(obj => obj.Member.ID || obj.MemberID)
                };
                mapFunc = mapEventCollisions;
            }
            if (!!variables) {
                getCalendarClient()
                    .query({
                        fetchPolicy: 'network-only',
                        query: searchCollidingStaffAllocations,
                        variables
                    })
                    .then(
                        ({ data }) => {
                            if (!data || !data.readStaffAllocations) {
                                onError('failed to get colliding staff allocations. data empty', data);
                                return;
                            }
                            const hasCollisions = mapFunc(input, data);
                            if (hasCollisions.length) {
                                const uniques = [
                                    ...new Set(
                                        hasCollisions.map(
                                            obj =>
                                                `${niceTimeFromString(obj.Start)} to ${niceTimeFromString(obj.End)}: ${
                                                    obj.Member.FirstName
                                                } ${obj.Member.Surname || ''} is already busy.`
                                        )
                                    )
                                ];
                                setCollisionMsg(uniques.map((obj, idx) => <li key={idx}>{obj}</li>));
                            } else {
                                onSave();
                            }
                        },
                        error => onError('failed to get colliding staff allocations.', error)
                    )
                    .finally(() => setBusy(false));
            } else {
                onSave();
                setBusy(false);
                onCloseModal();
            }
        },
        [onSave, onError, onCloseModal]
    );

    useEffect(() => {
        if (checkNow && !busy && !collisionMsg) {
            checkForCollisions(eventData);
        }
    }, [checkNow, busy, eventData, collisionMsg, checkForCollisions]);

    if (!checkNow) return null;

    const mapAppointmentCollisions = (input, data) => {
        return data.readStaffAllocations.filter(
            c => c.Appointment.ID !== input.ID && input.Members.find(m => m.ID === c.Member.ID)
        );
    };

    const mapEventCollisions = (input, data) => {
        const myAllocations = input.StaffAllocations.map(obj => obj.ID);
        return data.readStaffAllocations.filter(c => !myAllocations.includes(c.ID) && input.Funeral.ID !== c.FuneralID);
    };

    return (
        <Fragment>
            <Dialog
                disableEscapeKeyDown={true}
                aria-labelledby="Colliding Alert"
                aria-describedby="Colliding Alert"
                open={!!collisionMsg}
                onClose={onCloseModal}
            >
                <div className={classes.paper}>
                    <div className={classes.paperScroll}>
                        <ExclamationTriangleIcon
                            style={{ margin: 'auto', display: 'block', fontSize: '3em', color: '#eab35d' }}
                        />
                        <h4>These staff may have scheduling conflicts.</h4>
                        <ul style={{ paddingInlineStart: 20 }}>{collisionMsg}</ul>
                    </div>
                    <div className={cx(classes.buttonGroup, classes.actionButtons)}>
                        <Button onClick={onCloseModal} size="small" color={'primary'}>
                            make changes
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                onSave();
                                onCloseModal();
                            }}
                            className={cx(classes.buttonOk)}
                        >
                            Save Anyway
                        </Button>
                    </div>
                </div>
            </Dialog>
        </Fragment>
    );
};

const styles = ({ palette, breakpoints }) => ({
    buttonGroup: {
        marginTop: 12,
        clear: 'both',
        '& button': {
            marginRight: 12
        },
        '& button:last-child': {
            marginRight: 0
        }
    },
    popoverTitle: {
        margin: '0 6px'
    },
    paper: {
        maxWidth: '700px',
        width: '100%',
        maxHeight: 'calc(100vh - 32px)',
        backgroundColor: '#fafafa',
        padding: 25,
        borderRadius: 10,
        [breakpoints.down('sm')]: {
            padding: '16px 0'
        }
    },
    paperScroll: {
        overflow: 'hidden',
        overflowY: 'auto',
        maxHeight: 'calc(100vh - 248px)',
        padding: '0px 6px 12px 6px', //height: '650px',
        marginTop: 24
    },
    actionButtons: {
        textAlign: 'right'
    },
    buttonCancel: { color: '#E64040' },
    buttonOk: { color: '#26CC6F' },
    buttonClose: {
        position: 'absolute',
        top: '-1.5rem',
        right: '-1.5rem',
        '& > button > span > svg': {
            width: '30px',
            height: '30px'
        },
        '& > button': {
            background: palette.contentForeground.none,
            color: '#FFFFFF'
        }
    }
});

export default compose(withSnackbarMessage, withStyles(styles))(StaffScheduleConflictsPopup);
